/**
 * React Query Persistence Configuration
 *
 * Persists a BOUNDED slice of the query cache to localStorage for offline
 * support. Normal-size data survives an app reload and is available in flight
 * mode — including the /devices lists AND last-known values
 * (`device-telemetry`) of normal-size properties. Not offline: raw telemetry
 * series (sparklines, history charts), realtime/presence state, and anything
 * over the size guard (the big KNX properties' device lists).
 *
 * Why bounded — /devices "Page unresponsive" freeze: the cache is ONE JSON
 * blob, and it held the device lists of the big KNX properties (Sant Blai /
 * Es Revellar: device_telemetry_metadata / _categories / assignments, 2–6 MB
 * EACH, kept 24 h by gcTime). Past the ~5 MB localStorage quota the old
 * `removeOldestQuery` retry dropped ONE query per attempt and re-serialized
 * the WHOLE blob each time — 150+ multi-MB JSON.stringify calls in a single
 * main-thread task, re-run on every cache event. Guarantees now:
 *
 *  1. Raw telemetry series (chart / sparkline history) is never persisted
 *     (EXCLUDED_SEGMENTS).
 *  2. Any other query whose data + key is estimated > MAX_QUERY_CHARS is
 *     skipped. This is what drops the big properties' device lists while small
 *     properties keep offline /devices. The estimate is bounded + memoized —
 *     never a JSON.stringify per query (dehydrate runs the filter for EVERY
 *     query on EVERY cache event). Data that does not survive JSON (Map, Set,
 *     class instances, BigInt) is skipped too: a Map restored as `{}` crashed
 *     its consumer on the first render.
 *  3. The blob is trimmed to a budget BEFORE serializing — small entries (the
 *     app shell: profile, context, property list) first, then newest first —
 *     and hard-capped at MAX_BLOB_CHARS → one JSON.stringify per write.
 *  4. A failed write shrinks ONCE, then drops our blob and stops: at most two
 *     serializations per write — no retry loop can form.
 *  5. An unchanged persistable set is never re-written, so realtime patches to
 *     excluded queries and fetch-start events cost nothing; realtime-patched
 *     latest values are re-written at most once a minute.
 *  6. An oversized legacy blob is deleted on startup WITHOUT JSON.parse, and a
 *     corrupt blob restores as "no cache" instead of failing the restore.
 *  7. A failed background refetch (offline) keeps its last good data in the
 *     blob, as stale — one offline session no longer empties the next one.
 *
 * @module lib/queryPersister
 */

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { Query } from '@tanstack/react-query';
import type {
  PersistedClient,
  PersistQueryClientOptions,
  PersistRetryer,
  Persister,
} from '@tanstack/react-query-persist-client';

/** localStorage key of the persisted blob (clearQueryCache removes it on logout). */
export const QUERY_CACHE_STORAGE_KEY = 'ZENTRIAN_QUERY_CACHE';

/**
 * Per-query cap in estimated JSON chars (data + key) — same order as
 * widgetSnapshot's per-entry cap. A Sant Blai device list is ~3M chars.
 */
export const MAX_QUERY_CHARS = 256 * 1024;

/**
 * Hard cap for the whole blob, in chars. localStorage is ~5 MB per origin
 * (WebKit/iOS counts UTF-16 → ~2.6M chars) and is SHARED with the Supabase
 * session (sb-*-auth-token) and the widget snapshots — the query cache must
 * never be able to crowd those out.
 */
export const MAX_BLOB_CHARS = 1_000_000;

/** Estimated budget the blob is trimmed to before serializing (headroom under the hard cap for estimate error). */
const TRIM_BUDGET_CHARS = 800_000;

/** Budget for the single shrink after a failed write. */
const RETRY_BUDGET_CHARS = 250_000;

/** Approx. chars of per-query envelope (state fields, dehydratedAt, …) besides data and key. */
const QUERY_ENVELOPE_CHARS = 400;

/**
 * Trim pass 1 keeps entries up to this size (envelope included) first, within
 * SMALL_QUERY_SHARE of the budget. Login-time app-shell queries are small and
 * never refetch, so "newest first" alone always evicted them in favour of
 * polling widgets — an offline reload then had widgets but no property list.
 */
const SMALL_QUERY_CHARS = 16 * 1024;
const SMALL_QUERY_SHARE = 0.25;

/**
 * Change detection re-writes an unchanged blob once per bucket, so its
 * timestamp never ages past maxAge (24 h) while the app stays open.
 */
const FINGERPRINT_REFRESH_MS = 6 * 60 * 60 * 1000;

/**
 * Query-key segments whose data is patched by realtime many times a minute
 * (useDeviceTelemetry's setQueryData). They ARE persisted — /devices paints its
 * last-known values from them offline and on cold start — but change detection
 * counts their data version once per LIVE_PERSIST_INTERVAL_MS, so a realtime
 * stream cannot re-serialize the whole blob every throttle second.
 */
const LIVE_PATCHED_SEGMENTS = new Set<string>(['device-telemetry']);
const LIVE_PERSIST_INTERVAL_MS = 60 * 1000;

/**
 * Query-key SEGMENTS that must never be persisted (#3221 structural match).
 *
 * WHY structural: the old check substring-matched the JSON.stringify of the
 * whole key, so ANY future key or PARAM containing e.g. "session" was
 * silently excluded (['chat', { session: 1 }] never persisted), while
 * same-class keys that didn't contain the magic word slipped THROUGH —
 * found live: consumptionHistoryRollup / solarHistoryRollup are the same
 * quota-blowing chart-series class as rollupHistory but were persisted.
 *
 * A key is excluded when any of its STRING segments equals an entry below.
 * The set enumerates the real segment literals in use today (grepped), not
 * substrings — add the real segment name when adding a new sensitive key.
 *
 * Device LISTS (device-telemetry-metadata, telemetry-assignments, …) and the
 * latest values ('device-telemetry', see LIVE_PATCHED_SEGMENTS) are
 * deliberately NOT listed: they are small for normal properties and must stay
 * offline there; MAX_QUERY_CHARS drops the multi-MB ones.
 */
const EXCLUDED_SEGMENTS = new Set<string>([
  // auth/session (defensive — Supabase handles tokens itself)
  'auth',
  'session',
  // realtime / presence — websocket-fed, must never hydrate stale
  'realtime',
  'typing',
  'presence',
  'homePresence',
  // security scans — in-progress state must always be fresh
  'scan',
  'latest-scan',
  'security-scans',
  // sync operations
  'sync-queue',
  'sync-queue-stats',
  // access control — codes/events always fresh
  'accessCodes',
  'accessCode',
  'accessCodes-spv',
  'access-codes',
  'accessEvents',
  // large chart series — blow the localStorage quota and kill the whole
  // persisted blob; widgets fall back to snapshots (useWidgetSnapshot)
  'rollupHistory',
  'consumptionHistoryRollup',
  'solarHistoryRollup',
  // raw ThingsBoard telemetry series — one query per chart / TelemetryCard,
  // up to 1k–50k points each: a page of them alone would fill the budget and
  // evict the device lists and app shell
  'historicalTelemetry', // raw series, up to 50k points, 60 s refetch (useHistoricalTelemetry)
  'sparklineTelemetry', // raw sparkline series per TelemetryCard, 1k points (useHistoricalTelemetry)
]);

function hasSegmentIn(queryKey: readonly unknown[], segments: ReadonlySet<string>): boolean {
  return queryKey.some((segment) => typeof segment === 'string' && segments.has(segment));
}

/** A query is excluded when any string segment matches exactly. Exported for tests. */
export function isExcludedQueryKey(queryKey: readonly unknown[]): boolean {
  return hasSegmentIn(queryKey, EXCLUDED_SEGMENTS);
}

/** Size reported for data that does not survive JSON — above every cap. */
const NON_JSON_CHARS = Number.POSITIVE_INFINITY;

/**
 * Upper-bound-ish estimate of JSON.stringify(value).length that STOPS as soon
 * as the running total exceeds `budget`: cost is O(min(size, budget)), so a
 * 5 MB device list is rejected after ~budget chars of work instead of being
 * serialized. Escapes are not counted — the hard blob cap measures the real
 * string, so an under-estimate can never cause an oversized write.
 * Returns +Infinity for a value that does not survive a JSON round-trip, so
 * the size guard rejects it (see NON_JSON_CHARS).
 * Exported for tests.
 */
export function estimateJsonChars(value: unknown, budget: number): number {
  let total = 0;
  const stack: unknown[] = [value];
  while (stack.length > 0) {
    const node = stack.pop();
    if (typeof node === 'bigint') {
      return NON_JSON_CHARS; // JSON.stringify throws → would fail every write
    } else if (typeof node === 'string') {
      total += node.length + 2;
    } else if (typeof node === 'number') {
      total += Number.isFinite(node) ? String(node).length : 4; // NaN/Infinity → null
    } else if (typeof node === 'boolean') {
      total += node ? 4 : 5;
    } else if (node === null) {
      total += 4;
    } else if (Array.isArray(node)) {
      total += 2 + node.length; // brackets + separators
      if (total > budget) return total;
      for (let i = 0; i < node.length; i++) stack.push(node[i]);
    } else if (node instanceof Date) {
      total += 26; // toJSON → quoted ISO string
    } else if (typeof node === 'object') {
      // Map / Set / Blob / class instances serialize to `{}` (or lose their
      // methods) and restore as plain objects: useHealthScoresBatch's Map made
      // HealthPassCard throw on `.values()` before any refetch could run.
      const proto: unknown = Object.getPrototypeOf(node);
      if (proto !== Object.prototype && proto !== null) return NON_JSON_CHARS;
      const keys = Object.keys(node);
      total += 2 + keys.length * 4; // braces + quotes/colon/comma per key
      if (total > budget) return total;
      for (const key of keys) {
        total += key.length;
        if (total > budget) return total;
        stack.push((node as Record<string, unknown>)[key]);
      }
    }
    // undefined / function / symbol are dropped by JSON.stringify → 0
    if (total > budget) return total;
  }
  return total;
}

/**
 * React Query data is immutable (a new reference per update), so each payload
 * is measured once. Values above MAX_QUERY_CHARS are lower bounds.
 */
const dataSizeMemo = new WeakMap<object, number>();

function estimatedDataChars(data: unknown): number {
  if (typeof data !== 'object' || data === null) {
    return estimateJsonChars(data, MAX_QUERY_CHARS);
  }
  const cached = dataSizeMemo.get(data);
  if (cached !== undefined) return cached;
  const size = estimateJsonChars(data, MAX_QUERY_CHARS);
  dataSizeMemo.set(data, size);
  return size;
}

/** DEV-only: which (queryHash, dataUpdatedAt) the size guard already reported. */
const reportedOversized = new Set<string>();

function reportOversizedOnce(query: Pick<Query, 'queryHash' | 'state'>, chars: number): void {
  const id = `${query.queryHash}@${query.state.dataUpdatedAt}`;
  if (reportedOversized.has(id)) return;
  if (reportedOversized.size >= 500) reportedOversized.clear(); // keep the dev set bounded
  reportedOversized.add(id);
  const reason =
    chars === NON_JSON_CHARS
      ? 'not JSON-serializable (Map / Set / class instance / BigInt) — return plain arrays or records'
      : `≥ ~${Math.round(chars / 1024)} KB estimated (data + key), cap ${MAX_QUERY_CHARS / 1024} KB`;
  console.warn(
    `[queryPersister] not persisting ${query.queryHash.slice(0, 200)}: ${reason} — ` +
      'refetched after a reload, not available offline',
  );
}

/**
 * dehydrate() calls this for EVERY query on EVERY cache event (only the
 * storage write is throttled), so it must stay O(1) amortized: segment check,
 * status check, then a memoized size lookup. The key counts too — queryKey
 * AND queryHash are both written, and some keys embed long key lists.
 */
export function shouldPersistQuery(
  query: Pick<Query, 'queryKey' | 'queryHash' | 'state'>,
): boolean {
  // Structural segment match (#3221) — see EXCLUDED_SEGMENTS.
  if (isExcludedQueryKey(query.queryKey)) return false;
  // Only queries with data: 'success', or 'error' after a failed BACKGROUND
  // refetch — query-core keeps the data but flips the status, and dropping it
  // here emptied the blob after one offline session (see asLastGoodData).
  const { status, data } = query.state;
  if ((status !== 'success' && status !== 'error') || !data) return false;
  const chars = estimatedDataChars(data) + query.queryHash.length * 2;
  if (chars <= MAX_QUERY_CHARS) return true;
  if (import.meta.env.DEV) reportOversizedOnce(query, chars);
  return false;
}

type PersistedQuery = PersistedClient['clientState']['queries'][number];

function persistedQueryChars(query: PersistedQuery): number {
  // queryKey AND queryHash (the key's JSON) are both written.
  return (
    estimatedDataChars(query.state.data) + query.queryHash.length * 2 + QUERY_ENVELOPE_CHARS
  );
}

/**
 * Keep what fits `budget` by ESTIMATED size, in two passes, both newest first:
 *  1. small entries (≤ SMALL_QUERY_CHARS) up to SMALL_QUERY_SHARE of the
 *     budget — the cheap app shell survives however many widgets poll;
 *  2. everything else into the rest of the budget.
 * Serialization-free (memoized estimates), and returns the SAME object when
 * nothing needs trimming. Exported for tests.
 */
export function trimToBudget(client: PersistedClient, budget: number): PersistedClient {
  const { queries } = client.clientState;
  const sized = queries.map((query) => ({ query, size: persistedQueryChars(query) }));
  let total = 0;
  for (const { size } of sized) total += size;
  if (total <= budget) return client;

  const newestFirst = sized.sort((a, b) => b.query.state.dataUpdatedAt - a.query.state.dataUpdatedAt);
  const keep = new Set<PersistedQuery>();
  let used = 0;
  const smallBudget = budget * SMALL_QUERY_SHARE;
  for (const { query, size } of newestFirst) {
    if (size <= SMALL_QUERY_CHARS && used + size <= smallBudget) {
      used += size;
      keep.add(query);
    }
  }
  for (const { query, size } of newestFirst) {
    if (!keep.has(query) && used + size <= budget) {
      used += size;
      keep.add(query);
    }
  }
  return {
    ...client,
    clientState: { ...client.clientState, queries: queries.filter((q) => keep.has(q)) },
  };
}

/** Thrown by serialize when the blob would exceed MAX_BLOB_CHARS; the persister catches it and calls `retry`. */
export class PersistBlobTooLargeError extends Error {
  readonly chars: number;

  constructor(chars: number) {
    super(`persisted query cache would be ${chars} chars (cap ${MAX_BLOB_CHARS})`);
    this.name = 'PersistBlobTooLargeError';
    this.chars = chars;
  }
}

/**
 * A query whose background refetch failed is written as it looked before the
 * failure: success, but invalidated — it hydrates like any cached entry and
 * refetches on mount. The error itself is never written (it can hold request
 * details, and an Error does not round-trip anyway).
 */
function asLastGoodData(query: PersistedQuery): PersistedQuery {
  if (query.state.status !== 'error') return query;
  return {
    ...query,
    state: {
      ...query.state,
      status: 'success',
      error: null,
      fetchFailureCount: 0,
      fetchFailureReason: null,
      isInvalidated: true,
    },
  };
}

/**
 * Trim, then ONE plain JSON.stringify. No replacer: the old one (drop
 * functions / symbols) is exactly JSON.stringify's default and doubled its
 * cost. Exported for tests.
 */
export function serializePersistedClient(client: PersistedClient): string {
  const trimmed = trimToBudget(client, TRIM_BUDGET_CHARS);
  const { queries } = trimmed.clientState;
  const normalized = queries.some((q) => q.state.status === 'error')
    ? { ...trimmed, clientState: { ...trimmed.clientState, queries: queries.map(asLastGoodData) } }
    : trimmed;
  const json = JSON.stringify(normalized);
  if (json.length > MAX_BLOB_CHARS) throw new PersistBlobTooLargeError(json.length);
  return json;
}

/**
 * Parse a stored blob; anything unparseable or not client-shaped is "no
 * cache". Throwing here would reject the restore → PersistQueryClientProvider
 * calls onError instead of onSuccess, and main.tsx's initCapacitor never runs.
 */
function parsePersistedClient(raw: string): PersistedClient | undefined {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    const { clientState } = parsed as { clientState?: unknown };
    if (typeof clientState !== 'object' || clientState === null) return undefined;
    return parsed as PersistedClient;
  } catch {
    return undefined;
  }
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * getItem refuses (and deletes) a blob larger than MAX_BLOB_CHARS WITHOUT
 * parsing it: the pre-fix code wrote blobs up to the quota, and the library
 * JSON.parses before its buster/maxAge check — seconds of startup on mobile.
 * Reads/removes never throw (blocked storage restores as "no cache"); setItem
 * MUST throw so a quota error reaches `retry`.
 */
function createGuardedStorage(target: StorageLike): StorageLike {
  const removeQuietly = (key: string): void => {
    try {
      target.removeItem(key);
    } catch {
      // storage unavailable — nothing to free
    }
  };
  return {
    getItem: (key: string): string | null => {
      let raw: string | null;
      try {
        raw = target.getItem(key);
      } catch {
        return null;
      }
      if (raw !== null && raw.length > MAX_BLOB_CHARS) {
        removeQuietly(key);
        return null;
      }
      return raw;
    },
    setItem: (key: string, value: string): void => target.setItem(key, value),
    removeItem: removeQuietly,
  };
}

/**
 * What a write would contain: which queries at which data version (a
 * per-minute bucket for LIVE_PATCHED_SEGMENTS), the paused mutations' state,
 * plus a coarse time bucket. O(#queries), no data walk.
 */
function persistFingerprint(client: PersistedClient): string {
  const parts: string[] = [
    client.buster,
    String(Math.floor(Date.now() / FINGERPRINT_REFRESH_MS)),
  ];
  for (const m of client.clientState.mutations) {
    parts.push(`m:${m.state.status}@${m.state.submittedAt}${m.state.isPaused ? 'p' : ''}`);
  }
  for (const q of client.clientState.queries) {
    const version = hasSegmentIn(q.queryKey, LIVE_PATCHED_SEGMENTS)
      ? `~${Math.floor(q.state.dataUpdatedAt / LIVE_PERSIST_INTERVAL_MS)}`
      : `#${q.state.dataUpdateCount}@${q.state.dataUpdatedAt}`;
    parts.push(`${q.queryHash}${version}${q.state.isInvalidated ? '!' : ''}`);
  }
  return parts.join('|');
}

/**
 * The library persists on every added/removed/updated cache event of BOTH
 * caches — including fetch-start events and realtime setQueryData on EXCLUDED
 * queries. Skip the write when the persistable set is unchanged.
 */
function withChangeDetection(base: Persister): Persister {
  let lastFingerprint: string | null = null;
  return {
    persistClient: (client) => {
      const fingerprint = persistFingerprint(client);
      if (fingerprint === lastFingerprint) return;
      lastFingerprint = fingerprint;
      return base.persistClient(client);
    },
    restoreClient: () => base.restoreClient(),
    removeClient: () => {
      // The blob is gone — the next persist must write even if nothing changed.
      lastFingerprint = null;
      return base.removeClient();
    },
  };
}

/**
 * Bounded localStorage persister. Exported (with `throttleTime`) for tests.
 */
export function createBoundedQueryPersister(
  target: StorageLike | undefined,
  throttleTime = 1000,
): Persister {
  const storage = target ? createGuardedStorage(target) : undefined;

  // Replaces removeOldestQuery (one query per attempt + full re-serialize each
  // time = the freeze). Shrink ONCE; if that still fails, storage is full of
  // OTHER data (Supabase session, snapshots) or unavailable — drop our blob to
  // give them room and stop. At most two serializations per write.
  const retry: PersistRetryer = ({ persistedClient, errorCount }) => {
    if (errorCount === 1) {
      const smaller = trimToBudget(persistedClient, RETRY_BUDGET_CHARS);
      if (smaller !== persistedClient) return smaller;
    }
    storage?.removeItem(QUERY_CACHE_STORAGE_KEY);
    return undefined;
  };

  // The library types deserialize as always returning a client, but restore
  // treats a falsy result as "no cache" (hydrates nothing and still resolves),
  // which is exactly what a corrupt blob must be.
  const deserialize = (raw: string): PersistedClient | undefined => {
    const client = parsePersistedClient(raw);
    if (!client) storage?.removeItem(QUERY_CACHE_STORAGE_KEY);
    return client;
  };

  return withChangeDetection(
    createSyncStoragePersister({
      storage,
      key: QUERY_CACHE_STORAGE_KEY,
      throttleTime,
      serialize: serializePersistedClient,
      deserialize: deserialize as (raw: string) => PersistedClient,
      retry,
    }),
  );
}

/**
 * window.localStorage can THROW (storage blocked / sandboxed webview) or be
 * null (some Android WebViews) — never crash the app at import.
 */
function resolveStorage(): StorageLike | undefined {
  try {
    return typeof window !== 'undefined' ? (window.localStorage ?? undefined) : undefined;
  } catch {
    return undefined;
  }
}

/** The app's persister (localStorage). */
export const queryPersister: Persister = createBoundedQueryPersister(resolveStorage());

/**
 * Persistence options for the query client
 */
export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister: queryPersister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  // Busted automatically on every deploy: a stale blob hydrating old-shaped
  // data into new widget code crashed the dashboard to a blank page
  // (persist-hydration class — seen live with the box-scan→unifi presence
  // shape change, and intermittently after deploys). Cost: one cold cache
  // per deploy per user.
  buster: typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'v1',
  dehydrateOptions: {
    shouldDehydrateQuery: shouldPersistQuery,
  },
};
